import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './NewsPage.css';
import Header from '../components/Header';
import Footer from '../components/Footer';

const NewsPage = () => {
  const [expandedId, setExpandedId] = useState(null);
  const { t } = useTranslation();

  const [blogs, setBlogs] = useState(() => {
    try {
      const cached = sessionStorage.getItem('km_cached_blogs');
      return cached ? JSON.parse(cached) : [];
    } catch {
      return [];
    }
  });

  const [loading, setLoading] = useState(() => {
    try {
      const cached = sessionStorage.getItem('km_cached_blogs');
      return !cached;
    } catch {
      return true;
    }
  });

  const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.startsWith('192.168.') || window.location.port === '3000';
  const envApiUrl = process.env.REACT_APP_API_URL?.trim();
  const API_URL = envApiUrl && (isLocalHost || (!envApiUrl.includes("localhost") && !envApiUrl.includes("127.0.0.1"))) ? envApiUrl : '/api';

  const resolveImageUrl = (imgName) => {
    if (!imgName) return '';
    if (imgName.startsWith('http://') || imgName.startsWith('https://') || imgName.startsWith('data:')) {
      return imgName;
    }
    if (imgName.startsWith('/uploads/') || imgName.startsWith('uploads/')) {
      const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.startsWith('192.168.');
      const backendUrl = isLocalHost ? 'http://localhost:5000' : API_URL.replace('/api', '');
      return `${backendUrl}${imgName.startsWith('/') ? '' : '/'}${imgName}`;
    }
    const isLocalHost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' || window.location.hostname.startsWith('192.168.');
    const backendUrl = isLocalHost ? 'http://localhost:5000' : API_URL.replace('/api', '');
    return `${backendUrl}/uploads/${imgName}`;
  };

  useEffect(() => {
    let isMounted = true;
    const fetchPublishedBlogs = async () => {
      try {
        const res = await fetch(`${API_URL}/blogs`);
        const data = await res.json();
        if (data.success && Array.isArray(data.data)) {
          if (isMounted) {
            setBlogs(data.data);
            try {
              sessionStorage.setItem('km_cached_blogs', JSON.stringify(data.data));
            } catch (e) {}
          }
        }
      } catch (err) {
        console.error('Error fetching blogs:', err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchPublishedBlogs();
    return () => { isMounted = false; };
  }, [API_URL]);

  const getDisplayBlogs = () => {
    return blogs.map(b => ({
      id: b.id || b._id,
      date: new Date(b.publishedAt || b.createdAt).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' }),
      author: b.author || 'Admin',
      title: b.title,
      summary: b.content ? b.content.substring(0, 150) + '...' : '',
      content: b.content,
      image: b.thumbnail
    }));
  };

  const displayBlogs = getDisplayBlogs();

  const handleReadMore = (news) => {
    if (expandedId === news.id) {
      setExpandedId(null);
    } else {
      setExpandedId(news.id);
    }
  };

  return (
    <div className="news-page">
      <Header />
      
      <main className="news-main">
        <div className="container">
          <div className="news-header">
            <h1 className="news-title">{t('news_page.title')}</h1>
            <p className="news-subtitle">{t('news_page.subtitle')}</p>
          </div>
          
          {loading && displayBlogs.length === 0 ? (
            <div className="news-grid">
              {[1, 2, 3].map(i => (
                <div key={i} className="news-card" style={{ opacity: 0.7 }}>
                  <div style={{ width: '100%', height: '200px', background: 'linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%)', borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}></div>
                  <div className="news-content" style={{ display: 'flex', flexDirection: 'column', gap: '12px', padding: '20px' }}>
                    <div style={{ width: '60%', height: '14px', background: '#e2e8f0', borderRadius: '4px' }}></div>
                    <div style={{ width: '90%', height: '20px', background: '#cbd5e1', borderRadius: '4px' }}></div>
                    <div style={{ width: '100%', height: '40px', background: '#f1f5f9', borderRadius: '4px' }}></div>
                  </div>
                </div>
              ))}
            </div>
          ) : displayBlogs.length > 0 ? (
            <div className="news-grid">
            {displayBlogs.map((news) => (
              <div key={news.id} className="news-card">
                {news.image ? (
                  <img 
                    src={resolveImageUrl(news.image)} 
                    alt={news.title} 
                    className="news-card-image"
                    style={{ width: '100%', height: '200px', objectFit: 'cover', borderTopLeftRadius: '8px', borderTopRightRadius: '8px' }}
                  />
                ) : (
                  <div className="news-img-placeholder"></div>
                )}
                <div className="news-content">
                  <div className="news-meta">
                    <span className="meta-item">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>
                      {news.date}
                    </span>
                    <span className="meta-item">
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                      {news.author}
                    </span>
                  </div>
                  <h3 className="news-card-title">{news.title}</h3>
                  <p className="news-card-summary">
                    {expandedId === news.id ? news.content : news.summary}
                  </p>
                  <div className="news-card-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'auto' }}>
                    <button 
                      className="news-read-more" 
                      onClick={() => handleReadMore(news)}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', outline: 'none' }}
                    >
                      {expandedId === news.id ? t('news_page.btn_read_less') : t('news_page.btn_read_more')}
                    </button>
                    {expandedId === news.id && (
                      <button 
                        className="btn-enquire-small"
                        onClick={() => window.open(`https://wa.me/919631277264?text=I%20read%20your%20update:%20${encodeURIComponent(news.title)}`, '_blank')}
                        style={{ 
                          fontSize: '12px', 
                          padding: '6px 12px', 
                          background: '#4BA83D', 
                          color: 'white', 
                          border: 'none', 
                          borderRadius: '4px',
                          cursor: 'pointer'
                        }}
                      >
                        {t('news_page.btn_enquire')}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '80px 0', color: '#64748b' }}>
              <p style={{ fontSize: '16px', fontWeight: '500' }}>No news updates published yet.</p>
            </div>
          )}
        </div>
      </main>

      {/* WhatsApp FAB */}
      <div className="whatsapp-fab" onClick={() => window.open('https://wa.me/919631277264', '_blank')} style={{ cursor: 'pointer' }}>
        <svg viewBox="0 0 24 24" width="32" height="32" fill="white">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.438 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.414 0 .004 5.412 0 12.046c0 2.123.555 4.195 1.608 6.014L0 24l6.117-1.605a11.782 11.782 0 005.925 1.587h.005c6.632 0 12.043-5.414 12.047-12.048a11.821 11.821 0 00-3.526-8.513"/>
        </svg>
      </div>

      <Footer />
    </div>
  );
};

export default NewsPage;



